import { useState, useEffect, useCallback } from 'react';
import {
  Zap, RefreshCw, Trash2, CheckCircle,
  XCircle, Clock, AlertTriangle, Activity,
  RotateCcw, Key
} from 'lucide-react';
import { API_URL } from '../../services/api';


interface AccountStatus {
  username: string;
  isActive: boolean;
  onCooldown: boolean;
  cooldownRemainingMs: number | null;
  lastUsedAt: string | null;
  warmedAt: string | null;
  hasSession: boolean;
}

interface StatusSummary {
  total: number;
  ready: number;
  onCooldown: number;
  dead: number;
  noSession: number;
}

interface StatusResponse {
  success: boolean;
  summary: StatusSummary;
  accounts: AccountStatus[];
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function fmtTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function fmtCooldown(ms: number | null) {
  if (!ms) return null;
  const s = Math.ceil(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.ceil(s / 60)}m`;
}

export default function SessionManager() {
  const [locationSelect, setLocationSelect] = useState('in');
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [prewarmState, setPrewarmState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [prewarmMsg, setPrewarmMsg] = useState('');
  const [clearState, setClearState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [refreshingAccount, setRefreshingAccount] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importUsername, setImportUsername] = useState('');
  const [cookieInput, setCookieInput] = useState('');
  const [importing, setImporting] = useState(false);

  const activeLocation = locationSelect;

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sessions/status?location=${activeLocation}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStatus(data);
    } catch {
      // silently fail on background polls
    } finally {
      setLoading(false);
    }
  }, [activeLocation]);

  // Load status whenever the location changes
  useEffect(() => {
    setLoading(true);
    fetchStatus();
  }, [activeLocation, fetchStatus]);

  // Poll every 8 seconds while prewarm is running, otherwise every 30s
  useEffect(() => {
    const interval = setInterval(fetchStatus, prewarmState === 'running' ? 8000 : 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, prewarmState]);

  const handlePrewarm = async () => {
    setPrewarmState('running');
    setPrewarmMsg('Starting cold-start… Phase 1 (DB restore) runs instantly, Phase 2 (Puppeteer login for missing accounts) runs in background.');
    try {
      const res = await fetch(`${API_URL}/api/sessions/prewarm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ location: activeLocation }),
      });
      const data = await res.json();
      if (data.success) {
        setPrewarmState('done');
        setPrewarmMsg('Cold-start triggered. Sessions are loading — the table below will update automatically.');
        showToast('Cold-start triggered successfully');
        // Switch to fast polling to see accounts come online
        setTimeout(fetchStatus, 3000);
      } else {
        setPrewarmState('error');
        setPrewarmMsg(data.message || 'Unknown error');
        showToast(data.message || 'Failed to trigger prewarm', 'error');
      }
    } catch (err: any) {
      setPrewarmState('error');
      setPrewarmMsg(err.message);
      showToast('Network error — could not reach API', 'error');
    }
  };

  const handleClearAll = async () => {
    if (!confirm('This will wipe ALL saved sessions from the database and memory. Every account will need to re-login. Continue?')) return;
    setClearState('running');
    try {
      const res = await fetch(`${API_URL}/api/sessions/clear`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setClearState('done');
        showToast('All sessions cleared');
        setTimeout(() => { fetchStatus(); setClearState('idle'); }, 2000);
      } else {
        setClearState('error');
        showToast(data.message || 'Failed to clear sessions', 'error');
      }
    } catch (err: any) {
      setClearState('error');
      showToast('Network error', 'error');
    }
  };

  const handleRefreshAccount = async (username: string) => {
    setRefreshingAccount(username);
    try {
      const res = await fetch(`${API_URL}/api/sessions/refresh/${encodeURIComponent(username)}?location=${activeLocation}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Re-login started for @${username}`);
        setTimeout(fetchStatus, 5000);
      } else {
        showToast(data.message || 'Failed to refresh', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    } finally {
      setRefreshingAccount(null);
    }
  };

  const handleDeleteAccount = async (username: string) => {
    if (!confirm(`Delete saved session for ${username}? They will re-login on next scrape.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Session cleared for @${username}`);
        fetchStatus();
      } else {
        showToast(data.message || 'Failed', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    }
  };

  const handleImportCookie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cookieInput.trim()) {
      showToast('Please enter a cookie string or sessionid', 'error');
      return;
    }
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/sessions/import`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          username: importUsername,
          cookieString: cookieInput.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Session imported successfully for @${importUsername}`);
        setImportModalOpen(false);
        setCookieInput('');
        fetchStatus();
      } else {
        showToast(data.message || 'Failed to import session', 'error');
      }
    } catch {
      showToast('Network error — could not reach API', 'error');
    } finally {
      setImporting(false);
    }
  };

  const statusBadge = (account: AccountStatus) => {
    if (!account.isActive)
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-500/10 text-red-400 border border-red-500/20"><XCircle className="w-3 h-3" /> Dead</span>;
    if (account.onCooldown)
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"><Clock className="w-3 h-3" /> Cooldown {fmtCooldown(account.cooldownRemainingMs)}</span>;
    if (!account.hasSession)
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-500/10 text-gray-400 border border-gray-500/20"><AlertTriangle className="w-3 h-3" /> No Session</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-400 border border-green-500/20"><CheckCircle className="w-3 h-3" /> Ready</span>;
  };

  return (
    <div className="space-y-8">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-xl border transition-all
          ${toast.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
            Session Manager
          </h1>
          <p className="text-gray-400 mt-2">Manage Instagram bot account sessions — cold-start, monitor status, force refresh</p>
        </div>
        <button
          onClick={fetchStatus}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/20 transition-all"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Location Filter */}
      <div className="p-5 rounded-2xl border border-white/10 bg-black/20 backdrop-blur-md space-y-4">
        <label className="text-sm font-semibold text-gray-300 block">
          Manage Location / Proxy Group Bot Accounts
        </label>
        <div className="flex flex-wrap gap-4 items-center">
          <select
            value={locationSelect}
            onChange={(e) => setLocationSelect(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-white/15 bg-black/60 text-white font-medium outline-none focus:border-purple-500 transition-all text-sm min-w-[240px]"
          >
            <option value="in">🇮🇳 India (accounts_in.json)</option>
            <option value="us">🌐 Other (accounts_us.json)</option>
          </select>

          <span className="text-xs text-gray-400 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full uppercase font-semibold">
            Config file: accounts_{activeLocation}.json
          </span>
        </div>
      </div>

      {/* Summary cards */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="Total Accounts" value={status.summary.total} color="blue" />
          <SummaryCard label="Ready" value={status.summary.ready} color="green" />
          <SummaryCard label="On Cooldown" value={status.summary.onCooldown} color="yellow" />
          <SummaryCard label="Dead / No Session" value={status.summary.dead + status.summary.noSession} color="red" />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-4">

        {/* COLD START */}
        <button
          onClick={handlePrewarm}
          disabled={prewarmState === 'running'}
          className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm
            bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900 disabled:text-purple-400
            text-white transition-all shadow-lg shadow-purple-900/30 border border-purple-500/30"
        >
          {prewarmState === 'running'
            ? <><RotateCcw className="w-4 h-4 animate-spin" /> Cold-Starting…</>
            : <><Zap className="w-4 h-4" /> Cold Start All Sessions</>
          }
        </button>

        {/* CLEAR ALL */}
        <button
          onClick={handleClearAll}
          disabled={clearState === 'running'}
          className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm
            bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50
            text-red-400 hover:text-red-300 transition-all border border-red-500/20"
        >
          {clearState === 'running'
            ? <><RotateCcw className="w-4 h-4 animate-spin" /> Clearing…</>
            : <><Trash2 className="w-4 h-4" /> Clear All Sessions</>
          }
        </button>
      </div>

      {/* Prewarm status message */}
      {prewarmMsg && (
        <div className={`px-4 py-3 rounded-xl text-sm border
          ${prewarmState === 'error' ? 'bg-red-500/5 border-red-500/20 text-red-300' :
            prewarmState === 'done' ? 'bg-green-500/5 border-green-500/20 text-green-300' :
            'bg-purple-500/5 border-purple-500/20 text-purple-300'}`}>
          <div className="flex items-center gap-2">
            {prewarmState === 'running' && <Activity className="w-4 h-4 animate-pulse" />}
            {prewarmState === 'done' && <CheckCircle className="w-4 h-4" />}
            {prewarmState === 'error' && <XCircle className="w-4 h-4" />}
            {prewarmMsg}
          </div>
        </div>
      )}

      {/* Accounts table */}
      <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold">Bot Account Sessions</h2>
          <p className="text-gray-500 text-xs mt-0.5">Auto-refreshes every 30s. Faster polling while cold-start is active.</p>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading session data…</div>
        ) : !status || status.accounts.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No sessions in database yet.{' '}
            <button onClick={handlePrewarm} className="text-purple-400 hover:text-purple-300 underline">
              Run Cold Start
            </button>{' '}
            to seed them.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase tracking-wider border-b border-white/5">
                  <th className="px-6 py-3 text-left">Account</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Last Used</th>
                  <th className="px-6 py-3 text-left">Warmed At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {status.accounts.map(account => (
                  <tr key={account.username} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4 text-white font-mono text-xs">{account.username}</td>
                    <td className="px-6 py-4">{statusBadge(account)}</td>
                    <td className="px-6 py-4 text-gray-400 text-xs">{fmtTime(account.lastUsedAt)}</td>
                    <td className="px-6 py-4 text-gray-400 text-xs">{fmtTime(account.warmedAt)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {/* Force re-login */}
                        <button
                          onClick={() => handleRefreshAccount(account.username)}
                          disabled={refreshingAccount === account.username}
                          title="Force fresh Puppeteer login"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs
                            bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300
                            border border-blue-500/20 transition-all disabled:opacity-40"
                        >
                          {refreshingAccount === account.username
                            ? <RotateCcw className="w-3 h-3 animate-spin" />
                            : <RefreshCw className="w-3 h-3" />
                          }
                          Re-login
                        </button>
                        {/* Import cookie */}
                        <button
                          onClick={() => {
                            setImportUsername(account.username);
                            setCookieInput('');
                            setImportModalOpen(true);
                          }}
                          title="Manually import cookie / session ID"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs
                            bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300
                            border border-purple-500/20 transition-all"
                        >
                          <Key className="w-3 h-3" />
                          Import Cookie
                        </button>
                        {/* Delete session */}
                        <button
                          onClick={() => handleDeleteAccount(account.username)}
                          title="Clear this session from DB"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs
                            bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300
                            border border-red-500/20 transition-all"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {importModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-950 p-6 shadow-2xl space-y-6">
            <div>
              <h3 className="text-xl font-bold text-white">Import Session Cookie</h3>
              <p className="text-sm text-gray-400 mt-1">
                Directly inject session cookies for <span className="font-mono text-purple-400">@{importUsername}</span>.
              </p>
            </div>
            
            <form onSubmit={handleImportCookie} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-300">
                  Cookie String or raw Session ID
                </label>
                <textarea
                  value={cookieInput}
                  onChange={(e) => setCookieInput(e.target.value)}
                  placeholder="sessionid=123456789... or just paste the raw sessionid value"
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl border border-white/15 bg-black/40 text-white font-mono text-xs outline-none focus:border-purple-500 transition-all placeholder:text-gray-600"
                  required
                />
                <span className="text-[10px] text-gray-500 block">
                  Tip: Copy the whole cookie string from browser devtools, or just paste the value of the 'sessionid' cookie.
                </span>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setImportModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-400 hover:text-white border border-white/10 hover:border-white/20 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={importing}
                  className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900 disabled:text-purple-400 transition-all border border-purple-500/30"
                >
                  {importing ? 'Importing...' : 'Import Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: 'blue' | 'green' | 'yellow' | 'red' }) {
  const colorMap = {
    blue:   'text-blue-400   bg-blue-500/10   border-blue-500/20',
    green:  'text-green-400  bg-green-500/10  border-green-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
    red:    'text-red-400    bg-red-500/10    border-red-500/20',
  };
  return (
    <div className={`rounded-2xl border p-5 bg-black/40 backdrop-blur-md ${colorMap[color]}`}>
      <p className="text-gray-400 text-xs font-medium mb-1">{label}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}
